import {
  leadsStatusesCreateMutation,
  leadsStatusesDestroyMutation,
  leadsStatusesListOptions,
  leadsStatusesListQueryKey,
  leadsStatusesPartialUpdateMutation,
  leadsStatusesRetrieveOptions,
  LeadTypeReadable,
  PaginatedReturnLeadStatusListReadable,
  PatchedReturnLeadStatusReadable,
  ReturnLeadStatusReadable
} from '@crm/api'
import { ConfirmModal, DndTable } from '@crm/components'
import {
  CatalogActions,
  CatalogAddButton,
  FormModal,
  FormNomenclaturesLeadStatus
} from '@crm/features/nomenclatures/components'
import { useCapabilities } from '@crm/hooks/useCapabilities'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Form } from 'antd'
import { Edit, Trash } from 'iconsax-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'lead-status'

export const NomenclaturesLeadStatus = () => {
  const { canManageCatalogs } = useCapabilities()

  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()
  const { resetPagination } = useResetPagination()

  const [form] = Form.useForm()

  const [previousOrder, setPreviousOrder] = useState<number[]>([])
  const [data, setData] = useState<PaginatedReturnLeadStatusListReadable>()
  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [sorted, setSorted] = useState(false)

  const { data: statusList, isLoading } = useQuery(
    leadsStatusesListOptions({ query: { ...defaultFilters, ...params } })
  )

  useEffect(() => {
    if (statusList) {
      setData(statusList)
      setPreviousOrder(statusList?.results?.map(({ id }) => id))
    }
  }, [statusList])

  const { mutate: updateMutate } = useMutation({
    ...leadsStatusesPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([leadsStatusesListQueryKey()])
    }
  })

  const { mutate: deleteMutate } = useMutation({
    ...leadsStatusesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([leadsStatusesListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  useEffect(() => {
    if (data?.results && sorted) {
      const newOrder = data.results.map((item) => item.id)
      const changes = newOrder.reduce((acc, id, i) => {
        if (previousOrder[i] !== id) {
          const item = data.results.find((item) => item.id === id)
          if (item) {
            acc.push({
              ...item,
              order: i + 1,
              lead_type: item?.lead_type?.id as unknown as LeadTypeReadable
            })
          }
        }
        return acc
      }, [] as PatchedReturnLeadStatusReadable[])

      changes.forEach((item) =>
        updateMutate({ path: { id: Number(item.id) }, body: item })
      )

      setPreviousOrder(newOrder)
      setSorted(false)
    }
  }, [data, sorted, previousOrder, updateMutate])

  const onDelete = () => {
    if (deleteItem) {
      deleteMutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        icon: <Edit size={20} />,
        key: 'edit',
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        icon: <Trash size={20} />,
        danger: true,
        key: 'delete',
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const dndItems = data?.results?.map(({ id }) => id) || []

  const columns: TableColumnsType<ReturnLeadStatusReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.code'),
        dataIndex: 'code',
        key: 'code',
        sorter: true
      },
      {
        title: t('nomenclature.order'),
        dataIndex: 'order',
        key: 'order',
        sorter: true
      },
      {
        title: t('nomenclature.is_default'),
        key: 'is_default',
        sorter: true,
        render: ({ is_default }) =>
          is_default ? t('buttons.yes') : t('buttons.no')
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <CatalogActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  const onSortEnd = (
    action: (state: ReturnLeadStatusReadable[]) => ReturnLeadStatusReadable[]
  ) => {
    setData((prev) => {
      const results = action(prev?.results || [])

      return {
        ...prev,
        results: results || prev?.results
      } as PaginatedReturnLeadStatusListReadable
    })

    setSorted(true)
  }

  return (
    <>
      <DndTable
        disabled={!canManageCatalogs}
        onDragEnd={onSortEnd}
        rightChildren={
          <CatalogAddButton onClick={() => setIsCreate(true)}>
            {t('nomenclature.add_status')}
          </CatalogAddButton>
        }
        rowKey="id"
        items={dndItems}
        data={data}
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItem}
        form={form}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={leadsStatusesRetrieveOptions}
        onCreate={leadsStatusesCreateMutation}
        onUpdate={leadsStatusesPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclaturesLeadStatus form={form} />
      </FormModal>
    </>
  )
}
